/**
 * @file: BubbleQuoteSnapshot.tsx
 *
 * The quoted-message block shown inside a message that replies to another.
 * Two visual tones:
 *  - `standalone` — sits above a rich payload (image/card/…), neutral grey.
 *  - `bubble` — sits inside the colored text bubble, themed by `isMine`
 *    (light-on-blue for own messages, neutral otherwise).
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatQuoteSnapshot } from '@euler/model/chat';
import { FC, memo } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { MessagePayloadView, isRichPayloadType } from './MessagePayloadView';

export const BubbleQuoteSnapshot: FC<{
  quote: ChatQuoteSnapshot;
  tone: 'standalone' | 'bubble';
  isMine: boolean;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
}> = memo(({ quote, tone, isMine, onPress, style }) => {
  const { rpx } = useResponsiveContext();
  const themed = tone === 'bubble' && isMine;
  // Render the quoted message's actual payload compactly when it's a rich
  // kind (image/card/…); otherwise fall back to the text preview (also the
  // path for snapshots written before `payload` was captured).
  const rich = quote.payload != null && isRichPayloadType(quote.payload.type);
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.6}
      style={[
        {
          borderLeftWidth: rpx(3),
          borderLeftColor: themed ? 'rgba(255,255,255,0.5)' : '#9ca3af',
          paddingLeft: rpx(8),
          paddingVertical: rpx(4),
          backgroundColor: themed
            ? 'rgba(255,255,255,0.12)'
            : 'rgba(0,0,0,0.04)',
          borderRadius: rpx(4),
        },
        style,
      ]}
    >
      <Typography.Text
        size={rpx(11)}
        color={themed ? '#dbeafe' : '#6b7280'}
        style={
          tone === 'bubble'
            ? { fontFamily: FontFamily.NotoSans.Medium }
            : undefined
        }
      >
        {quote.author?.name ?? '消息'}
      </Typography.Text>
      {rich && quote.payload ? (
        // Purely visual — let taps fall through to the quote's own onPress
        // (jump to original) instead of the renderer's inner touchables.
        <View style={{ marginTop: rpx(4) }} pointerEvents="none">
          <MessagePayloadView payload={quote.payload} isMine={isMine} quoted />
        </View>
      ) : (
        <Typography.Text
          size={rpx(12)}
          color={themed ? '#e0e7ff' : '#4b5563'}
          numberOfLines={2}
          style={{ marginTop: rpx(2) }}
        >
          {quote.preview}
        </Typography.Text>
      )}
    </TouchableOpacity>
  );
});
